import { decideUnauthenticatedAccess } from '~/utils/sessionReauth'

function isAuthLandingPath(path: string): boolean {
  return path === '/' || path === '/sign-in' || path === '/sign-up'
}

export default defineNuxtRouteMiddleware(async (to) => {
  const { isAuthenticated, isSuperAdmin, ready, loadSession } = useAuth()

  // Hydrates cached user synchronously then returns — does not wait on /auth/me
  // when a cookie/localStorage snapshot exists (avoids a multi-second login flash).
  if (!ready.value) {
    await loadSession()
  }
  else if (import.meta.client && !isAuthenticated.value) {
    await loadSession()
  }

  // Store subdomains serve the shop at `/`. Never bounce that to Wazzafni.
  const onStorefront = Boolean(currentStoreSubdomain())
  const { landingPath } = useSiteMode()

  // `/` lands on the platform picked in Settings. Vntru opens the shops page.
  if (!onStorefront && to.path === '/') {
    const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : ''
    if (isAuthenticated.value && redirect.startsWith('/app')) {
      return navigateTo(redirect, { replace: true })
    }
    return navigateTo(landingPath.value, { replace: true })
  }

  // Logged-in users must never stay on the public auth pages.
  if (!onStorefront && isAuthLandingPath(to.path) && isAuthenticated.value) {
    const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : ''
    if (redirect.startsWith('/app')) {
      return navigateTo(redirect, { replace: true })
    }
    return navigateTo(landingPath.value, { replace: true })
  }

  const isAppRoute = to.path === '/app' || to.path.startsWith('/app/')

  if (isAppRoute && !isAuthenticated.value && !isGuestAllowedPath(to.path)) {
    // Guest or expired session: keep this URL and cover it with the login popup.
    // Intentional visits to `/` and `/sign-in` still use the login page.
    if (decideUnauthenticatedAccess({
      toPath: to.path,
      authenticated: false,
      guestAllowed: false,
    }) === 'popup') {
      try {
        useAuthModal().openAuth('signin', { blocking: true })
      }
      catch {
        // Popup state is shared; the page still must not leave.
      }
    }
  }

  // Guests land on their platform's page rather than the personalised home mix.
  if (!isAuthenticated.value && to.path === '/app/home') {
    return navigateTo(landingPath.value, { replace: true })
  }

  // The admin dashboard is reserved for super-admins.
  if (to.path.startsWith('/app/admin') && !isSuperAdmin.value) {
    return navigateTo('/app/home')
  }
})
